import api from '../axios.config';
import { API_ENDPOINTS, createEndpoint } from './endpoints';

/**
 * Senior Level Flow — candidate-facing, token-authenticated endpoints (PR #748 §3).
 *
 * There is no bearer auth: the signed link `token` (from the page URL) is passed
 * as a `?token=` query param on every call. Mirrors ResumeService.getProfilePublic
 * — `api` unwraps to the response body, and the payload sits under `data`.
 */
const withToken = (endpoint, token) =>
  `${endpoint}?token=${encodeURIComponent(token)}`;

const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

export const SeniorFlowService = {
  // GET landing info for the current status.
  getLanding: async (token) => {
    const response = await api.get(
      withToken(API_ENDPOINTS.SENIOR_FLOW.LANDING, token)
    );
    return unwrap(response);
  },

  // POST the candidate's yes/no to the outreach.
  acknowledge: async (token, accepted) => {
    const response = await api.post(
      withToken(API_ENDPOINTS.SENIOR_FLOW.ACKNOWLEDGE, token),
      { accepted }
    );
    return unwrap(response);
  },

  // PATCH a flow-scoped profile section (shared { key, data } contract).
  updateProfileSection: async (token, key, data) => {
    const response = await api.patch(
      withToken(API_ENDPOINTS.SENIOR_FLOW.PROFILE, token),
      { key, data }
    );
    return unwrap(response);
  },

  // POST interview acceptance (no body).
  acceptInterview: async (token) => {
    const response = await api.post(
      withToken(API_ENDPOINTS.SENIOR_FLOW.ACCEPT_INTERVIEW, token)
    );
    return unwrap(response);
  },

  // POST interview decline (no body).
  declineInterview: async (token) => {
    const response = await api.post(
      withToken(API_ENDPOINTS.SENIOR_FLOW.DECLINE_INTERVIEW, token)
    );
    return unwrap(response);
  },

  // ── Protected mirrors (bearer auth, no token — scoped by sec_mapping_id).
  // Same payload shapes as the magic-link endpoints above; used when this
  // flow is opened from the authenticated "My Applications" list instead of
  // an email link.
  getLandingProtected: async (secMappingId) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.LANDING, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.get(endpoint);
    return unwrap(response);
  },

  acknowledgeProtected: async (secMappingId, accepted) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.ACKNOWLEDGE, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.post(endpoint, { accepted });
    return unwrap(response);
  },

  updateProfileSectionProtected: async (secMappingId, key, data) => {
    const endpoint = createEndpoint(API_ENDPOINTS.MY_PIPELINES.PROFILE, {
      sec_mapping_id: secMappingId,
    });
    const response = await api.patch(endpoint, { key, data });
    return unwrap(response);
  },
};

export default SeniorFlowService;
